/**
 * The shapes a saying crosses an untrusted boundary in.
 *
 * Shared because both ends have to agree about them: the picker refuses a submission the API
 * would refuse, the API validates against the same bounds, and the *protocol* builds its input
 * field out of `sayingIdSchema` rather than a second copy of "how long an id may be".
 *
 * Nothing here validates that an id *exists*. That is a question about the catalogue and about
 * one player's approved list, which is `resolveSayingText`'s job and the simulation's — a schema
 * that enumerated the catalogue would mean a protocol bump every time a saying was added, which
 * is the trade `powerUpIdSchema` and `miniGameChoiceSchema` already refused.
 */

import { z } from 'zod';
import {
  MAX_CUSTOM_SAYINGS_PER_USER,
  MAX_SAYING_LIST_LENGTH,
  MIN_SAYING_LIST_LENGTH,
  SAYING_ID_MAX_LENGTH,
  SAYING_STATUSES,
  SAYING_TEXT_MAX_LENGTH,
  SAYING_TEXT_MIN_LENGTH,
  SAYING_VERDICTS,
} from '../constants/sayings.js';

/** A saying id: a catalogue slug or an approved submission's uuid. Bounded, never enumerated. */
export const sayingIdSchema = z.string().min(1).max(SAYING_ID_MAX_LENGTH);

/**
 * The words somebody typed. Trimmed before the length check, so a submission of three spaces is
 * refused rather than stored — the rule `banInputSchema.reason` is under, for the same reason.
 */
export const sayingTextSchema = z
  .string()
  .trim()
  .min(SAYING_TEXT_MIN_LENGTH)
  .max(SAYING_TEXT_MAX_LENGTH);

/**
 * A player's whole list, as they want it ordered.
 *
 * Sent whole rather than as a diff: "remove this one" and "move this one up" are the same
 * mutation to a list, and a list of at most MAX_SAYING_LIST_LENGTH short ids is smaller than the
 * patch language that would avoid re-sending it.
 */
export const sayingListSchema = z
  .array(sayingIdSchema)
  .min(MIN_SAYING_LIST_LENGTH)
  .max(MAX_SAYING_LIST_LENGTH);

export const sayingStatusSchema = z.enum(SAYING_STATUSES);
export const sayingVerdictSchema = z.enum(SAYING_VERDICTS);

/**
 * The approved custom sayings sealed into a WebSocket ticket.
 *
 * Parsed rather than trusted even though this process signed it: a ticket outlives a deployment's
 * own constants by up to WS_TICKET_TTL_SECONDS, and the verifier's rule for every other claim is
 * that a malformed one is *dropped* rather than thrown. Capped here at the same number the
 * database enforces, so a hand-built token cannot make a room resolve against a list of ten
 * thousand.
 */
export const ticketSayingsSchema = z
  .array(z.object({ id: sayingIdSchema, text: sayingTextSchema }))
  .max(MAX_CUSTOM_SAYINGS_PER_USER);
